import { Component } from '@theme/component';
import { lock, unlock, clearBodyLocks } from 'tua-body-scroll-lock';
import { updateHeaderHeights } from '@theme/utilities';

const CLASSES = {
  IS_OPEN: 'is-open',
  IS_SUB_OPEN: 'is-sub-open',
  IS_GROUP_OPEN: 'is-group-open',
  IS_ASIDE_OPEN: 'is-aside-open',
  IS_ASIDE_SUB_OPEN: 'is-aside-sub-open',
  IS_MENU_MOBILE_OPEN: 'is-menu-mobile-open',
  IS_SUB_SELECTED: 'is-sub-selected',
  IS_CAMPAIGN_ITEM_VISIBLE: 'is-visible',
  IS_ASIDE_CAMPAIGN_ITEM_ACTIVE: 'is-aside-campaign-item-active',
};

const SELECTORS = {
  GROUP: '.js-mega-menu-group',
  ASIDE: '.js-mega-menu-aside',
  DRAWER: '.js-mega-menu-drawer',
  DRAWER_CONTENT: '.js-mega-menu-drawer-content',
  MOBILE_LINK: '.js-mega-menu-mobile-trigger',
  SUB_LINKS: '.mega-menu-sub-links',
  SUB_MENU_TRIGGER: '.js-mega-menu-sub-menu-trigger',
  IMAGE_GRID_CONTENT: '.js-mega-menu-image-grid-content',
  CAMPAIGN_ITEM: '.js-mega-menu-campaign-item',
  HEADER: 'header-main',
  HEADER_MENU_BUTTON: '[data-menu-key]',
};

const VALUES = {
  DESKTOP_BREAKPOINT: 720,
};

const ATTRIBUTES = {
  MENU_KEY: 'data-menu-key',
  GROUP_KEY: 'data-group-key',
  SUBMENU_KEY: 'data-submenu-key',
  GROUP_LINK_KEY: 'data-group-link-key',
  IS_MENU_SCROLLED: 'is-menu-scrolled',
  CAMPAIGN_ITEM_KEY: 'data-campaign-item-key',
};

const SETTINGS = {
  OPEN_FIRST_SUBMENU: false,
};

/**
 * Attributes: id="mega-menu" required for external on:click targeting.
 */
class MegaMenu extends Component {
  connectedCallback() {
    super.connectedCallback();

    this.drawerEl = this.querySelector(SELECTORS.DRAWER);
    this.drawerContentEl = this.drawerEl?.querySelector(SELECTORS.DRAWER_CONTENT);

    this.#initDefaultState();
  }

  isOpended() {
    return this.classList.contains(CLASSES.IS_OPEN);
  }

  isMobile() {
    return window.innerWidth < VALUES.DESKTOP_BREAKPOINT;
  }

  toggle() {
    this.classList.add(CLASSES.IS_OPEN);
    this.#toggleHeaderScrolled();

    lock(this.drawerContentEl);
    this.drawerContentEl?.scrollTo({ top: 0 });
  }

  close() {
    this.classList.remove(CLASSES.IS_OPEN);
    this.#toggleHeaderScrolled();

    clearBodyLocks();

    for (const button of document.querySelectorAll(SELECTORS.HEADER_MENU_BUTTON)) {
      button.setAttribute('aria-expanded', 'false');
    }

    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (this.drawerEl && !reducedMotion) {
      this.drawerEl.addEventListener('transitionend', () => this.#resetToDefaultState(), {
        once: true,
      });
    } else {
      this.#resetToDefaultState();
    }
  }

  getCampaignItemEls(/** @type {Element} */ asideEl) {
    if (!asideEl) return [];

    return [...asideEl.querySelectorAll(SELECTORS.CAMPAIGN_ITEM)];
  }

  selectGroup(/** @type {{ groupKey?: string }} */ { groupKey } = {}, /** @type {Event} */ event) {
    if (!groupKey) return;

    if (event && event.preventDefault) {
      event.preventDefault();
    }

    this.classList.add(CLASSES.IS_OPEN);
    updateHeaderHeights();
    this.#toggleHeaderScrolled();
    this.#activateGroup(groupKey);

    const imageGridContentEl = this.querySelector(
      `${SELECTORS.ASIDE}[${ATTRIBUTES.GROUP_KEY}="${groupKey}"] ${SELECTORS.IMAGE_GRID_CONTENT}`,
    );

    clearBodyLocks();

    if (imageGridContentEl) {
      lock([this.drawerContentEl, imageGridContentEl]);
    } else {
      lock(this.drawerContentEl);
    }

    const headerMenuButtonEls = document.querySelectorAll(SELECTORS.HEADER_MENU_BUTTON);

    if (!headerMenuButtonEls.length) {
      return;
    }

    for (const button of headerMenuButtonEls) {
      button.setAttribute(
        'aria-expanded',
        String(button.getAttribute(ATTRIBUTES.MENU_KEY) === groupKey),
      );
    }

    if (this.isMobile()) {
      return;
    }

    if (SETTINGS.OPEN_FIRST_SUBMENU) {
      const firstTrigger = this.querySelector(
        `${SELECTORS.GROUP}[${ATTRIBUTES.GROUP_KEY}="${groupKey}"] ${SELECTORS.SUB_MENU_TRIGGER}`,
      );
      if (firstTrigger instanceof HTMLElement) {
        this.#openSubMenuByTrigger(firstTrigger);
      }
    }
  }

  openSubMenu(/** @type {Event} */ event) {
    const triggerEl = /** @type {HTMLElement} */ (event.target)?.closest(
      SELECTORS.SUB_MENU_TRIGGER,
    );

    if (triggerEl instanceof HTMLElement) {
      this.#openSubMenuByTrigger(triggerEl);
    }
  }

  #openSubMenuByTrigger(/** @type {HTMLElement} */ triggerEl) {
    const groupKey = triggerEl.closest(SELECTORS.GROUP)?.getAttribute(ATTRIBUTES.GROUP_KEY);

    if (!groupKey) return;

    const groupEl = this.querySelector(`${SELECTORS.GROUP}[${ATTRIBUTES.GROUP_KEY}="${groupKey}"]`);

    if (!groupEl) return;

    const asideEl = this.querySelector(`${SELECTORS.ASIDE}[${ATTRIBUTES.GROUP_KEY}="${groupKey}"]`);

    if (!asideEl) return;

    const subLinksEl = asideEl.querySelector(SELECTORS.SUB_LINKS);

    if (!subLinksEl) return;

    const submenuTitle = triggerEl.getAttribute(ATTRIBUTES.SUBMENU_KEY);
    const subGroupEls = [...subLinksEl.querySelectorAll(`[${ATTRIBUTES.GROUP_LINK_KEY}]`)];

    const campaignItemEls = this.getCampaignItemEls(asideEl);

    const activeSubGroup =
      subGroupEls.find((ul) => ul.getAttribute(ATTRIBUTES.GROUP_LINK_KEY) === submenuTitle) ??
      subGroupEls[0];

    const activeCampaignItem = campaignItemEls.find(
      (img) => img.getAttribute(ATTRIBUTES.CAMPAIGN_ITEM_KEY) === submenuTitle,
    );

    for (const campaignItemEl of campaignItemEls) {
      campaignItemEl.classList.remove(CLASSES.IS_CAMPAIGN_ITEM_VISIBLE);
    }

    for (const subGroupEl of subGroupEls) {
      subGroupEl.classList.toggle(CLASSES.IS_SUB_OPEN, subGroupEl === activeSubGroup);
    }

    activeCampaignItem?.classList.add(CLASSES.IS_CAMPAIGN_ITEM_VISIBLE);

    for (const trigger of groupEl?.querySelectorAll(SELECTORS.SUB_MENU_TRIGGER) ?? []) {
      trigger.classList.toggle(CLASSES.IS_SUB_SELECTED, trigger === triggerEl);
    }

    subLinksEl.classList.add(CLASSES.IS_SUB_OPEN);

    unlock(this.drawerContentEl);
    lock(asideEl);

    if (activeCampaignItem) {
      asideEl.classList.add(CLASSES.IS_ASIDE_CAMPAIGN_ITEM_ACTIVE);
    } else {
      asideEl.classList.remove(CLASSES.IS_ASIDE_CAMPAIGN_ITEM_ACTIVE);
    }

    asideEl.classList.add(CLASSES.IS_ASIDE_SUB_OPEN);
    triggerEl.setAttribute('aria-expanded', 'true');

    asideEl?.scrollTo({ top: 0 });
  }

  closeSubMenu(/** @type {Event} */ event) {
    const subLinksEl = /** @type {HTMLElement} */ (event.target)?.closest(SELECTORS.SUB_LINKS);

    if (!subLinksEl) return;

    const asideEl = subLinksEl.closest(SELECTORS.ASIDE);

    if (!asideEl) return;

    unlock(asideEl);
    lock(this.drawerContentEl);

    const campaignItemEls = this.getCampaignItemEls(asideEl);

    for (const campaignItemEl of campaignItemEls) {
      campaignItemEl.classList.remove(CLASSES.IS_CAMPAIGN_ITEM_VISIBLE);
    }

    subLinksEl.classList.remove(CLASSES.IS_SUB_OPEN);
    asideEl?.classList.remove(CLASSES.IS_ASIDE_SUB_OPEN);
    asideEl?.classList.remove(CLASSES.IS_ASIDE_CAMPAIGN_ITEM_ACTIVE);
    const groupKey = asideEl?.getAttribute(ATTRIBUTES.GROUP_KEY);

    if (!groupKey) return;

    const groupEl = this.querySelector(`${SELECTORS.GROUP}[${ATTRIBUTES.GROUP_KEY}="${groupKey}"]`);
    for (const trigger of groupEl?.querySelectorAll(SELECTORS.SUB_MENU_TRIGGER) ?? []) {
      trigger.setAttribute('aria-expanded', 'false');
      trigger.classList.remove(CLASSES.IS_SUB_SELECTED);
    }
  }

  #initDefaultState() {
    this.querySelector(SELECTORS.GROUP)?.classList.add(CLASSES.IS_GROUP_OPEN);
    this.querySelector(SELECTORS.ASIDE)?.classList.add(CLASSES.IS_ASIDE_OPEN);
    this.querySelector(SELECTORS.MOBILE_LINK)?.classList.add(CLASSES.IS_MENU_MOBILE_OPEN);
  }

  #resetToDefaultState() {
    for (const subLinksEl of this.querySelectorAll(
      `${SELECTORS.SUB_LINKS}.${CLASSES.IS_SUB_OPEN}`,
    )) {
      subLinksEl.classList.remove(CLASSES.IS_SUB_OPEN);
      subLinksEl.closest(SELECTORS.ASIDE)?.classList.remove(CLASSES.IS_ASIDE_SUB_OPEN);
      subLinksEl.closest(SELECTORS.ASIDE)?.classList.remove(CLASSES.IS_ASIDE_CAMPAIGN_ITEM_ACTIVE);
    }

    for (const ul of this.querySelectorAll(
      `[${ATTRIBUTES.GROUP_LINK_KEY}].${CLASSES.IS_SUB_OPEN}`,
    )) {
      ul.classList.remove(CLASSES.IS_SUB_OPEN);
    }

    for (const triggerEl of this.querySelectorAll(SELECTORS.SUB_MENU_TRIGGER)) {
      triggerEl.setAttribute('aria-expanded', 'false');
      triggerEl.classList.remove(CLASSES.IS_SUB_SELECTED);
    }

    for (const secondaryLevelImageEl of [...this.querySelectorAll(SELECTORS.CAMPAIGN_ITEM)]) {
      if (secondaryLevelImageEl.classList.contains(CLASSES.IS_CAMPAIGN_ITEM_VISIBLE)) {
        secondaryLevelImageEl.classList.remove(CLASSES.IS_CAMPAIGN_ITEM_VISIBLE);
      }
    }

    const firstGroupKey = this.querySelector(SELECTORS.GROUP)?.getAttribute(ATTRIBUTES.GROUP_KEY);

    if (firstGroupKey) {
      this.#activateGroup(firstGroupKey);
    }
  }

  #activateGroup(/** @type {string} */ key) {
    for (const group of this.querySelectorAll(SELECTORS.GROUP)) {
      group.classList.toggle(
        CLASSES.IS_GROUP_OPEN,
        group.getAttribute(ATTRIBUTES.GROUP_KEY) === key,
      );
    }

    for (const aside of this.querySelectorAll(SELECTORS.ASIDE)) {
      aside.classList.toggle(
        CLASSES.IS_ASIDE_OPEN,
        aside.getAttribute(ATTRIBUTES.GROUP_KEY) === key,
      );
    }

    for (const mobile of this.querySelectorAll(SELECTORS.MOBILE_LINK)) {
      mobile.classList.toggle(
        CLASSES.IS_MENU_MOBILE_OPEN,
        mobile.getAttribute(ATTRIBUTES.MENU_KEY) === key,
      );
    }
  }

  #toggleHeaderScrolled() {
    if (this.isMobile()) {
      return;
    }

    const headerEl = document.querySelector(SELECTORS.HEADER);

    if (!headerEl) {
      return;
    }

    if (this.isOpended()) {
      headerEl.setAttribute(ATTRIBUTES.IS_MENU_SCROLLED, '');
    } else {
      headerEl.removeAttribute(ATTRIBUTES.IS_MENU_SCROLLED);
    }
  }
}

if (!customElements.get('mega-menu')) {
  customElements.define('mega-menu', MegaMenu);
}
